import React, { useEffect, useState } from 'react';
import API, { ENV, PADDLE_CLIENT_TOKEN, PUBLIC_URL } from '../utils/const';
import { CheckoutEventNames, Paddle, PaddleEventData, initializePaddle } from '@paddle/paddle-js';
import { Price } from '@paddle/paddle-js/types/shared/shared';
import LoadingOverlay from '../components/LoadingOverlay';
import { MessageKind, refreshPage, setMessage } from '../utils/common';
import { createToast } from '../components/Toast';
import { PaddlePricing } from '../components/PaddlePricing';
import AppLayout from '../layouts/AppLayout';
import { Box, Button, Center, Container, Group, Loader, Modal, Paper, Stack, Text, Title } from '@mantine/core';
import { handleApiError } from '../utils/app';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Head } from 'vite-react-ssg';
import { format_total, parseTotal } from '../utils/paddle';
import { PaddleSubscription } from '../utils/paddle';
import { Team } from '../utils/team';
import { TeamComponent } from '../components/TeamComponent';
import { MagicLinkConnect } from '../components/MagicLinkConnect';
import Payment from './Payment';


const InnerPlan: React.FC = () => {
  const { t } = useTranslation(["app"]);
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<PaddleSubscription | null>(null);
  const [pricingLoaded, setPricingLoaded] = useState<boolean>(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const { isAuthenticated, user, getLoggedUser, setRedirectOnUnauthenticated } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [paddleInitError, setPaddleInitError] = useState<string | null>(null);
  const [paddleInitAttempted, setPaddleInitAttempted] = useState<boolean>(false);

  useEffect(() => {
    setRedirectOnUnauthenticated(true);
  }, []);

  const updateStatePricing = () => {
    setPricingLoaded(true);
  };

  async function verifyTransactionId(transaction_id: string) {
    const response = await fetch(`${API}/paddle/verify_transaction?transaction_id=${transaction_id}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      return false
    }
    return true
  }

  async function paddleCallback(event: PaddleEventData) {
    if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {

      paddle?.Checkout.close();
      setIsLoading(true);
      try {
        const data = event.data!;
        const transaction_id = data.transaction_id;

        if (await verifyTransactionId(transaction_id)) {
          setMessage("You have succesfully subscribed!", MessageKind.Success);
          window.location.href = `${PUBLIC_URL}/plan`
        } else {
          createToast(`Failed to verify transaction ${transaction_id} - this should mean no payment was made. If you believe there was, please email at support@calensync.live`, MessageKind.Error);
        }
      } finally {
        setIsLoading(false);
      }
    }
  }

  function buy(price: Price) {
    let args: any = {
      items: [{ priceId: price.id, quantity: 1 }]
    }
    if (user!.customer_id !== null) {
      args.customer = { id: user!.customer_id }
    }
    paddle?.Checkout.open(args);
  }

  const setupPaddle = async () => {
    try {
      setPaddleInitAttempted(true);
      const paddleInstance = await initializePaddle({
        environment: ENV === "production" ? "production" : "sandbox",
        token: PADDLE_CLIENT_TOKEN,
        eventCallback: paddleCallback,
        pwCustomer: { id: user!.customer_id }
      });
      if (paddleInstance) {
        setPaddle(paddleInstance);
      } else {
        const errorMsg = "Failed to initialize Paddle.js";
        setPaddleInitError(errorMsg);
        createToast(errorMsg, MessageKind.Error);
        console.error(`${errorMsg}. User-agent: ${navigator.userAgent}`);
      }
    } catch (e) {
      const errorMsg = `Failed to initialize Paddle.js: ${e instanceof Error ? e.message : String(e)}`;
      setPaddleInitError(errorMsg);
      createToast(`Failed to initialize Paddle.js`, MessageKind.Error);
      console.error(`${errorMsg}. User-agent: ${navigator.userAgent}`);
    }
  }

  const openUpdatePaymentMethod = () => {
    window.open(subscription?.management_urls.update_payment_method, '_blank');
  }

  useEffect(() => {
    if (isAuthenticated !== null) {
      Promise.all([
        setupPaddle(),
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);



  const getPricingOrSubscription = async () => {
    if (user == null) {
      return
    }
    if (user!.subscription_id !== null) {
      const response = await fetch(`${API}/paddle/subscription`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        createToast("Error occured while fetching your subscription. If this continues, please contact support.", MessageKind.Error);
        return;
      }
      let data: PaddleSubscription = await response.json();
      data.next_billed_at = new Date(data.next_billed_at);
      setSubscription(data)
    } else {
      if (user!.transaction_id !== null) {
        let iteration = 0;
        createToast("Transaction confirmed, verifying your subscription", MessageKind.Info);
        while (iteration < 3) {
          await verifyTransactionId(user!.transaction_id);
          await getLoggedUser();
          // wait one second before trying again
          await new Promise(resolve => setTimeout(resolve, 1000));
          iteration += 1;
        }
        createToast("We were unable to confirm your subscription, but the transaction is confirmed. Please check this page again later", MessageKind.Error);
      }
      else {
        setShowPricing(true);
      }
    }
  }


  const cancelSubscription = async () => {
    const response = await fetch(`${API}/subscription/cancel`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      handleApiError(response, t, true)
      return;
    }
    refreshPage()
  }

  useEffect(() => {
    getPricingOrSubscription().finally(() => {
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);


  return (
    <>
      {isLoading &&
        <LoadingOverlay />
      }
      <Modal opened={showCancel} onClose={() => setShowCancel(false)} title={t("dashboard.team.cancel-subscription")}>
        <Stack gap='md'>
          <Text>{t("dashboard.team.cancel-confirmation")}</Text>
          <Group grow>
            <Button variant='outline' onClick={() => setShowCancel(false)}>{t("common.cancel")}</Button>
            <Button variant='outline' color='red' onClick={cancelSubscription}>{t("dashboard.team.cancel-subscription")}</Button>
          </Group>
        </Stack>
      </Modal>
      <div className='content mt-4 pt-4'>
        {
          <>
            {subscription != null && subscription?.items != null && (team == null || (team != null && team.is_owner)) &&
              <Container p={4} size='lg'>
                <Paper withBorder p='md'>
                  <Group justify='space-between' align='center'>
                    <div>
                      <Title order={3} fw={400}>{subscription?.items[0].price.description}</Title>
                    </div>
                    <Group gap='xs'>
                      <Button variant='outline' color='primary' onClick={() => openUpdatePaymentMethod()}>{t("dashboard.team.update-payment-method")}</Button>
                      {subscription?.next_transaction != null &&
                        <Button variant='outline' color='red' onClick={() => setShowCancel(true)}>{t("dashboard.team.cancel-subscription")}</Button>
                      }
                    </Group>
                  </Group>
                  {subscription?.next_transaction != null &&
                    <>
                      <Text size='sm' c='dimmed'>{t("dashboard.team.next-billing", { date: subscription?.next_billed_at.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) })}</Text>
                      <Text size='sm' c='dimmed'>{t("dashboard.team.amount", { amount: format_total(parseTotal(subscription?.next_transaction?.details.totals.grand_total), subscription?.next_transaction.details.totals.currency_code) })}</Text>
                    </>
                  }
                  {subscription?.next_transaction == null && subscription?.current_billing_period?.ends_at != null &&
                    <p className='text-muted'>{t("dashboard.team.subscription-cancelled", { date: new Date(subscription?.current_billing_period?.ends_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) })}</p>
                  }
                  {subscription?.next_transaction == null && subscription?.current_billing_period == null &&
                    <p className='text-muted'>Subscription was cancelled {new Date(subscription?.cancelled_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} and is not active anymore</p>
                  }
                </Paper>
              </Container>
            }
            {subscription != null && subscription.items == null &&
              <Container p={4} size='lg'>
                <Paper withBorder p='md'>
                  <Title order={3} fw={400}>{t("dashboard.team.subscription-error")}</Title>
                  <p>{t("dashboard.team.contact-support")}</p>
                </Paper>
              </Container>
            }
          </>
        }
        {paddle != null && showPricing === true &&
          <PaddlePricing paddle={paddle} clickedBuy={buy} isHome={false} isReady={updateStatePricing} size={'md'} />
        }
        {!paddle && !paddleInitError && (
          <Box>
            <Center>
              <Group><Loader />{t("plan.initializing-payment-provider")}</Group>
            </Center>
          </Box>
        )}
        {paddleInitAttempted && paddleInitError && showPricing && !paddle && (
          <Container p={4} size='lg'>
            <Paper withBorder p='md' style={{ marginBottom: '20px' }}>
              <Title order={3} fw={400} c="red">{t("plan.paddle-error")}</Title>
              <Text>
                {t("plan.paddle-error-description")}
              </Text>
              <Stack mt="md">
                <Text fw={500}>{t("plan.troubleshooting-tips")}:</Text>
                <ul>
                  <li>{t("plan.disable-adblocker")}</li>
                  <li>{t("plan.try-different-browser")} <MagicLinkConnect /></li>
                  <li>{t("plan.check-network-connection")}</li>
                  <li>{t("plan.contact-support")}</li>
                </ul>
                <Center>
                  <Button onClick={() => window.location.reload()} mt="sm" maw={200}>
                    {t("plan.try-again")}
                  </Button>
                </Center>
              </Stack>
            </Paper>
          </Container>
        )}
        <TeamComponent team={team} setTeam={setTeam} user={user} subscription={subscription} />
      </div>
    </>
  );
};


const Plan: React.FC = () => {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  if (params?.get('_ptxn')) {
    return <Payment />;
  }
  return <AppLayout>
    <Head>
      <title>Plan | Calensync</title>
      <meta name="description" content="Manage your Calensync subscription and your team" />
    </Head>
    <InnerPlan />
  </AppLayout>
}

export default Plan;
